"use client";
import { useEffect, useMemo, useState } from "react";
import { ActionIcon, Drawer, Group, Text } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import useDeviceType from "@/app/_hooks/useDeviceType";
import { useSupportContext } from "@/app/_providers/SupportProvider/SupportProvider";
import { SupportTicket } from "@/app/@types/support";
import SupportStatusBadge from "./SupportStatusBadge";
import SupportTicketList from "./SupportTicketList";
import SupportTicketDetail from "./SupportTicketDetail";
import NewSupportTicketForm from "./NewSupportTicketForm";

type SupportView = "list" | "detail" | "new";

const SupportDrawer = () => {
  const { isMobile } = useDeviceType();
  const { isOpen, close, tickets, isLoading, initialTicketId } =
    useSupportContext();

  const [view, setView] = useState<SupportView>("list");
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
    null
  );

  // Reset to the requested view each time the drawer opens.
  useEffect(() => {
    if (!isOpen) return;
    if (initialTicketId) {
      setSelectedTicketId(initialTicketId);
      setView("detail");
    } else {
      setSelectedTicketId(null);
      setView("list");
    }
  }, [isOpen, initialTicketId]);

  // Always read the live ticket from the subscription so status / unread
  // changes flow into the detail view.
  const selectedTicket: SupportTicket | null = useMemo(
    () => tickets.find((t) => t.uid === selectedTicketId) ?? null,
    [tickets, selectedTicketId]
  );

  // If the selected ticket disappears (should not happen, but be safe) fall
  // back to the list.
  useEffect(() => {
    if (view === "detail" && !isLoading && !selectedTicket) {
      setView("list");
    }
  }, [view, isLoading, selectedTicket]);

  const goToList = () => {
    setSelectedTicketId(null);
    setView("list");
  };

  const title = (() => {
    if (view === "detail" && selectedTicket) {
      return (
        <Group gap="sm" wrap="nowrap">
          <Text fz="inherit" fw="inherit">
            Ticket #{selectedTicket.ticketNumber}
          </Text>
          <SupportStatusBadge status={selectedTicket.status} size="lg" />
        </Group>
      );
    }
    if (view === "new") return "New ticket";
    return "Support";
  })();

  return (
    <Drawer
      position="right"
      opened={isOpen}
      onClose={close}
      size={isMobile ? "100%" : 700}
      title={
        <Group gap="xs" wrap="nowrap">
          {view !== "list" && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={goToList}
              aria-label="Back to tickets"
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
          )}
          <Text fz="h2" fw={700}>
            {title}
          </Text>
        </Group>
      }
      withCloseButton
      styles={{
        content: { display: "flex", flexDirection: "column" },
        body: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: 0,
        },
      }}
    >
      {view === "list" && (
        <SupportTicketList
          tickets={tickets}
          isLoading={isLoading}
          onSelect={(ticket) => {
            setSelectedTicketId(ticket.uid);
            setView("detail");
          }}
          onNew={() => setView("new")}
        />
      )}
      {view === "detail" && selectedTicket && (
        <SupportTicketDetail ticket={selectedTicket} />
      )}
      {view === "new" && (
        <NewSupportTicketForm
          openTickets={tickets.filter((t) => t.status !== "resolved")}
          onOpenTicket={(ticket) => {
            setSelectedTicketId(ticket.uid);
            setView("detail");
          }}
          onCreated={(ticket) => {
            setSelectedTicketId(ticket.uid);
            setView("detail");
          }}
        />
      )}
    </Drawer>
  );
};

export default SupportDrawer;
